import { SPARK_RESOURCE_TYPES } from 'constants/common';
import toastr from 'react-hot-toast';
import { useMutation, useQueryClient } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';

// These are the fields we're updating via PATCH request.
// Some of these fields might not exist in the current type definition
// but they are supported by the API according to the requirements.
type UpdateMarketplaceAccountVariables = {
  id: string;
  description?: string | null;
  website?: string | null;
  linkedIn?: string | null; // Custom social field for the MarketplaceAccount (camelCase with capital I)
  x?: string | null; // Custom social field for the MarketplaceAccount (Twitter)
  youtube?: string | null; // Custom social field for the MarketplaceAccount
  facebook?: string | null; // Custom social field for the MarketplaceAccount
  imageURL?: string | null; // Profile/avatar image URL
  displayName?: string | null;
  countryID?: string | number | null;
  email?: string | null;
  phone?: string | null;
};

export function useUpdateMarketplaceAccount() {
  const queryClient = useQueryClient();

  const updateMarketplaceAccount = ({ id, ...attributes }: UpdateMarketplaceAccountVariables) => {
    if (!id) {
      return Promise.reject('Marketplace Account ID cannot be null');
    }

    const body = {
      data: {
        id,
        type: SPARK_RESOURCE_TYPES.MARKETPLACE_ACCOUNTS,
        attributes
      }
    };

    return fetchWrapper({
      url: `${SPARK_RESOURCE_TYPES.MARKETPLACE_ACCOUNTS}/${id}`,
      method: 'PATCH',
      body
    });
  };

  return useMutation(
    'updateMarketplaceAccount',
    updateMarketplaceAccount,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['marketplaceAccount']);
        toastr.success('Profile updated');
      },
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`Failed to update Marketplace account: ${error.message}`);
        }
      }
    }
  );
} 